/* global axios */

const normalizeBase = apiUrl => {
  if (!apiUrl) return '';
  const trimmed = apiUrl.replace(/\/+$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
};

class WahaAPI {
  static requestHeaders(adminToken) {
    return {
      'X-Api-Key': adminToken,
      'Content-Type': 'application/json',
    };
  }

  static async listSessions(
    apiUrl,
    adminToken,
    { includeStopped = false } = {}
  ) {
    if (!apiUrl || !adminToken) {
      return Promise.reject(new Error('Missing Waha credentials'));
    }

    const url = `${normalizeBase(apiUrl)}/sessions`;

    try {
      const response = await axios.get(url, {
        headers: this.requestHeaders(adminToken),
        params: includeStopped ? { all: true } : {},
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data?.message || 'Failed to fetch Waha sessions'
        );
      }
      throw new Error('Network error while fetching Waha sessions');
    }
  }

  static async deleteSession(apiUrl, adminToken, sessionName) {
    if (!apiUrl || !adminToken || !sessionName) {
      return Promise.reject(new Error('Missing Waha credentials'));
    }

    const url = `${normalizeBase(apiUrl)}/sessions/${sessionName}`;

    try {
      const response = await axios.delete(url, {
        headers: this.requestHeaders(adminToken),
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data?.message || 'Failed to delete Waha session'
        );
      }
      throw new Error('Network error while deleting Waha session');
    }
  }

  static async logoutSession(apiUrl, adminToken, sessionName) {
    if (!apiUrl || !adminToken || !sessionName) {
      return Promise.reject(new Error('Missing Waha credentials'));
    }

    const url = `${normalizeBase(apiUrl)}/sessions/logout`;

    try {
      const response = await axios.post(
        url,
        { name: sessionName },
        {
          headers: {
            ...this.requestHeaders(adminToken),
            Accept: 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data?.message || 'Failed to log out Waha session'
        );
      }
      throw new Error('Network error while logging out Waha session');
    }
  }

  static async stopSession(apiUrl, adminToken, sessionName, logout = false) {
    if (!apiUrl || !adminToken || !sessionName) {
      return Promise.reject(new Error('Missing Waha credentials'));
    }

    const url = `${normalizeBase(apiUrl)}/sessions/stop`;

    try {
      const response = await axios.post(
        url,
        { name: sessionName, logout },
        {
          headers: {
            ...this.requestHeaders(adminToken),
            Accept: 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data?.message || 'Failed to stop Waha session'
        );
      }
      throw new Error('Network error while stopping Waha session');
    }
  }

  static async restartSession(apiUrl, adminToken, sessionName) {
    if (!apiUrl || !adminToken || !sessionName) {
      return Promise.reject(new Error('Missing Waha credentials'));
    }

    const url = `${normalizeBase(apiUrl)}/sessions/${sessionName}/restart`;

    try {
      const response = await axios.post(
        url,
        {},
        {
          headers: {
            ...this.requestHeaders(adminToken),
            Accept: 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data?.message || 'Failed to restart Waha session'
        );
      }
      throw new Error('Network error while restarting Waha session');
    }
  }

  static async fetchQrCode(apiUrl, adminToken, sessionName) {
    if (!apiUrl || !adminToken || !sessionName) {
      return Promise.reject(new Error('Missing Waha credentials'));
    }

    const url = `${normalizeBase(apiUrl)}/${sessionName}/auth/qr`;

    try {
      const response = await axios.get(url, {
        headers: {
          ...this.requestHeaders(adminToken),
          Accept: 'image/png',
        },
        params: { format: 'raw' },
        responseType: 'json',
      });
      const payload = response.data;

      if (typeof payload === 'string') {
        return payload;
      }

      if (payload && typeof payload === 'object') {
        return (
          payload.base64 ||
          payload.qrcode?.base64 ||
          payload.qrcode?.code ||
          payload.qrcode ||
          payload.code ||
          payload.value ||
          null
        );
      }

      if (payload instanceof ArrayBuffer) {
        const bytes = new Uint8Array(payload);
        const binary = bytes.reduce(
          (acc, byte) => acc + String.fromCharCode(byte),
          ''
        );
        return btoa(binary);
      }

      return null;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data?.message || 'Failed to fetch Waha QR code'
        );
      }
      throw new Error('Network error while fetching Waha QR code');
    }
  }

  // ==========================================
  // Chatwoot WhatsApp Controller Integration
  // These methods call Chatwoot's WhatsApp controller endpoints
  // which internally communicate with WAHA or Evolution API
  // ==========================================

  static buildChatwootUrl(accountId, inboxId, action) {
    return `/api/v1/accounts/${accountId}/inboxes/${inboxId}/whatsapp/${action}`;
  }

  // Check if phone numbers exist on WhatsApp
  static async checkNumber(accountId, inboxId, numbers) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'check_number');
    try {
      const response = await axios.post(url, {
        numbers: Array.isArray(numbers) ? numbers : [numbers],
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to check phone numbers'
      );
    }
  }

  // Send a reaction emoji to a message
  static async sendReaction(accountId, inboxId, messageId, emoji) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_reaction');
    try {
      const response = await axios.post(url, {
        message_id: messageId,
        emoji,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send reaction');
    }
  }

  // Send a poll/survey message
  static async sendPoll(
    accountId,
    inboxId,
    conversationId,
    question,
    options,
    maxAnswers = 1
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_poll');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        question,
        options,
        max_answers: maxAnswers,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send poll');
    }
  }

  // Send a location message
  static async sendLocation(
    accountId,
    inboxId,
    conversationId,
    { latitude, longitude, name, address }
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_location');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        latitude,
        longitude,
        name,
        address,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send location');
    }
  }

  // Send a contact/vCard message
  static async sendContact(accountId, inboxId, conversationId, contact) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_contact');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        contact,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send contact');
    }
  }

  // Send a sticker message
  static async sendSticker(accountId, inboxId, conversationId, stickerUrl) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_sticker');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        sticker_url: stickerUrl,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send sticker');
    }
  }

  // Get WhatsApp user information
  static async getUserInfo(accountId, inboxId, numbers) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'get_user_info');
    try {
      const response = await axios.post(url, {
        numbers: Array.isArray(numbers) ? numbers : [numbers],
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to get user info');
    }
  }

  // Get WhatsApp user avatar URL
  static async getAvatar(accountId, inboxId, number, preview = false) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'get_avatar');
    try {
      const response = await axios.post(url, {
        number,
        preview,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to get avatar');
    }
  }

  // Send typing indicator
  static async sendTyping(
    accountId,
    inboxId,
    conversationId,
    state = 'typing'
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_typing');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        state,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to send typing indicator'
      );
    }
  }

  // Send presence status
  static async sendPresence(
    accountId,
    inboxId,
    conversationId,
    state,
    isAudio = false
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_presence');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        state,
        is_audio: isAudio,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send presence');
    }
  }

  // Forward a message to another conversation
  static async forwardMessage(accountId, inboxId, messageId, toConversationId) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'forward_message');
    try {
      const response = await axios.post(url, {
        message_id: messageId,
        to_conversation_id: toConversationId,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to forward message'
      );
    }
  }

  // Star or unstar a message
  static async starMessage(accountId, inboxId, messageId, star = true) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'star_message');
    try {
      const response = await axios.post(url, {
        message_id: messageId,
        star,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to update message star'
      );
    }
  }

  // Block a contact
  static async blockContact(
    accountId,
    inboxId,
    { conversationId, contactId } = {}
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'block_contact');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        contact_id: contactId,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to block contact');
    }
  }

  // Unblock a contact
  static async unblockContact(
    accountId,
    inboxId,
    { conversationId, contactId } = {}
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'unblock_contact');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        contact_id: contactId,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to unblock contact'
      );
    }
  }

  // Reject an incoming call
  static async rejectCall(accountId, inboxId, callId) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'reject_call');
    try {
      const response = await axios.post(url, {
        call_id: callId,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to reject call');
    }
  }

  // Mark messages as read
  static async markRead(accountId, inboxId, conversationId, messageIds = null) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'mark_read');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        message_ids: messageIds,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to mark messages as read'
      );
    }
  }

  // Archive or unarchive a chat
  static async archiveChat(accountId, inboxId, conversationId, archive = true) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'archive_chat');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        archive,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to archive chat');
    }
  }

  // Get list of blocked contacts
  static async getBlockedContacts(accountId, inboxId) {
    const url = this.buildChatwootUrl(
      accountId,
      inboxId,
      'get_blocked_contacts'
    );
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to get blocked contacts'
      );
    }
  }

  // Send a message with custom link preview
  static async sendLinkPreview(
    accountId,
    inboxId,
    conversationId,
    { text, previewUrl, title, description, imageUrl }
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_link_preview');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        text,
        preview_url: previewUrl,
        title,
        description,
        image_url: imageUrl,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to send link preview'
      );
    }
  }
}

export default WahaAPI;
